import { useState, useCallback } from "react";
import { useCatalog } from "../../hooks/use-catalog";
import { useCartStore } from "../../store/cart";
import type { CatalogItem, CatalogVariation } from "../../api/types";
import "./item-grid.css";

function fmt(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

export function ItemGrid() {
  const { items, loading, error } = useCatalog();
  const addItem = useCartStore((s) => s.addItem);
  const [search, setSearch] = useState("");
  const [selectedVariations, setSelectedVariations] = useState<
    Record<string, string>
  >({});

  const filtered = search
    ? items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()))
    : items;

  const handleSelectItem = useCallback(
    (item: CatalogItem) => {
      const selectedVarId = selectedVariations[item.id];
      const variation: CatalogVariation =
        item.variations.find((v) => v.id === selectedVarId) ||
        item.variations[0];

      addItem({
        itemId: item.id,
        variationId: variation.id,
        name: item.name,
        variationName: variation.name,
        basePriceCents: variation.price_cents,
        originalPriceCents: variation.original_price_cents,
      });
    },
    [addItem, selectedVariations],
  );

  return (
    <>
      <div className="pos-search">
        <div className="search-wrap">
          <span className="search-icon">&#x1F50D;</span>
          <input
            className="search-input"
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="item-grid">
        {loading && <div className="item-loading">Loading catalog...</div>}

        {error && (
          <div className="item-loading" style={{ color: "var(--red)" }}>
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="item-loading">No items found</div>
        )}

        {filtered.map((item) => {
          const selectedVarId = selectedVariations[item.id];
          const displayVar =
            item.variations.find((v) => v.id === selectedVarId) ||
            item.variations[0];

          return (
            <div
              key={item.id}
              className="item-card"
              onClick={() => handleSelectItem(item)}
            >
              <div className="item-name">{item.name}</div>
              <div className="item-price">
                {fmt(displayVar?.price_cents ?? 0)}
              </div>
              {item.variations.length > 1 && (
                <select
                  className="item-var-select"
                  value={selectedVarId || item.variations[0].id}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    setSelectedVariations((prev) => ({
                      ...prev,
                      [item.id]: e.target.value,
                    }));
                  }}
                >
                  {item.variations.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} — {fmt(v.price_cents)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
